import type {
  MaterialNodeSpec,
  Quantity,
  Constituent,
  IOMapping,
} from '../../graph/domain/types';
import { convertToUnit } from './transferHelpers';

// ── IO Mapping validation ───────────────────────────────────────────────────

const FRACTION_SUM_TOLERANCE = 0.001;

/**
 * Validate that all fractions in the IO mapping are in (0,1] and that
 * the total fraction donated by any single input does not exceed 1.0.
 */
export function validateIOMapping(mapping: IOMapping[]): string | null {
  for (const m of mapping) {
    if (m.fraction <= 0 || m.fraction > 1) {
      return `Fraction must be between 0 and 1, got ${m.fraction}.`;
    }
  }

  // Per-input sum must be ≤ 1.0
  const sumByInput = new Map<string, number>();
  for (const m of mapping) {
    sumByInput.set(m.inputMaterialId, (sumByInput.get(m.inputMaterialId) ?? 0) + m.fraction);
  }
  for (const [inputId, sum] of sumByInput) {
    if (sum > 1 + FRACTION_SUM_TOLERANCE) {
      return `Total fraction for input ${inputId} is ${sum}, exceeds 1.0.`;
    }
  }
  return null;
}

// ── Quantity computation from IO mapping ────────────────────────────────────

/**
 * Compute the output quantity for an output material based on IO mapping
 * donations from input materials PLUS the output's existing quantity.
 * 
 * Formula: output_new_qty = output_existing_qty + sum(input_qty * fraction for each donor)
 */
export function computeOutputQuantityFromMapping(
  outputMaterialId: string,
  ioMapping: IOMapping[],
  inputMaterials: Record<string, MaterialNodeSpec>,
  outputMaterials: Record<string, MaterialNodeSpec>,
): Quantity | null {
  const donors = ioMapping.filter((m) => m.outputMaterialId === outputMaterialId);
  if (donors.length === 0) return null;

  const firstWithQty = donors.find((d) => inputMaterials[d.inputMaterialId]?.quantity);
  if (!firstWithQty) return null;

  const targetUnits = inputMaterials[firstWithQty.inputMaterialId].quantity!.value.units;
  const targetType = inputMaterials[firstWithQty.inputMaterialId].quantity!.type;

  // Start with the output's existing quantity
  let total = 0;
  const outputMaterial = outputMaterials[outputMaterialId];
  if (outputMaterial?.quantity) {
    total = convertToUnit(
      outputMaterial.quantity.value.amount,
      outputMaterial.quantity.value.units,
      targetUnits,
    );
  }

  // Add donations from all input materials
  for (const { inputMaterialId, fraction } of donors) {
    const input = inputMaterials[inputMaterialId];
    if (!input?.quantity) continue;
    const converted = convertToUnit(
      input.quantity.value.amount * fraction,
      input.quantity.value.units,
      targetUnits,
    );
    total += converted;
  }

  return { value: { amount: total, units: targetUnits }, type: targetType };
}

// ── Constituent merge from IO mapping ───────────────────────────────────────

/**
 * Merge constituents flowing into an output material based on IO mapping.
 * Weights are normalized to sum to 1.
 */
export function mergeConstituentsFromMapping(
  outputMaterialId: string,
  ioMapping: IOMapping[],
  inputMaterials: Record<string, MaterialNodeSpec>,
): Constituent[] {
  const donors = ioMapping.filter((m) => m.outputMaterialId === outputMaterialId);
  const accum = new Map<string, number>();
  let totalWeight = 0;

  for (const { inputMaterialId, fraction } of donors) {
    const input = inputMaterials[inputMaterialId];
    if (!input?.quantity) continue;
    const massForDonation = input.quantity.value.amount * fraction;
    if (massForDonation === 0) continue;

    for (const c of input.constituents) {
      const contribution = massForDonation * c.weight;
      accum.set(c.name, (accum.get(c.name) ?? 0) + contribution);
      totalWeight += contribution;
    }
  }

  if (totalWeight === 0) return [];
  return Array.from(accum.entries()).map(([name, weight]) => ({
    name,
    weight: weight / totalWeight,
  }));
}

// ── Reminder quantity calculation ────────────────────────────────────────────

/**
 * Compute the remainder quantity for an input material in a COMPLEX step.
 *
 * The reminder represents what's left of the input after it has donated
 * portions to the outputs via IO mapping.
 *
 * @param inputMaterialId The input material ID
 * @param ioMapping Array of IOMapping entries defining donations
 * @param inputMaterial The input material with original quantity
 * @returns Quantity representing what remains (original - donated)
 */
export function computeInputReminderQuantity(
  inputMaterialId: string,
  ioMapping: IOMapping[],
  inputMaterial: MaterialNodeSpec,
): Quantity | null {
  if (!inputMaterial.quantity) return null;

  // Find all donations FROM this input
  const donations = ioMapping.filter((m) => m.inputMaterialId === inputMaterialId);
  if (donations.length === 0) {
    // No donations, remainder = original quantity
    return inputMaterial.quantity;
  }

  // Sum all fractions donated by this input
  const totalFractionDonated = donations.reduce((sum, d) => sum + d.fraction, 0);

  // Remainder = original - (original * donated_fraction)
  const originalAmount = inputMaterial.quantity.value.amount;
  const remainderAmount = originalAmount * (1 - totalFractionDonated);

  return {
    value: {
      amount: Math.max(0, remainderAmount), // Don't go negative
      units: inputMaterial.quantity.value.units,
    },
    type: inputMaterial.quantity.type,
  };
}

// ── Reminder nodes for COMPLEX operations ───────────────────────────────────

/**
 * Build a reminder node for a COMPLEX step input.
 *
 * A reminder node tracks what remains of an input material after it has
 * contributed to the COMPLEX operation. It is positioned as an output of
 * the COMPLEX node and represents the "leftover" state of the input.
 *
 * @param inputMaterial The input material that fed into the COMPLEX operation
 * @param complexOperationId The ID of the COMPLEX operation
 * @returns MaterialNodeSpec for the reminder node
 */
export function buildInputReminderNode(
  inputMaterial: MaterialNodeSpec,
  complexOperationId: string,
): MaterialNodeSpec {
  return {
    name: inputMaterial.name,
    container: { ...inputMaterial.container, id: '' },
    constituents: [...inputMaterial.constituents],
    sourceMaterials: [
      {
        sourceVertexId: inputMaterial.id!,
        name: inputMaterial.name,
        container: inputMaterial.container,
        notes: null,
      },
    ],
    quantity: inputMaterial.quantity,
    symbolicQuantity: 'remainder',
    isReminder: true,
    isEmpty: inputMaterial.isEmpty,
    stateFlags: inputMaterial.stateFlags,
  };
}
